import React from 'react';

import {
  Title,
  Paper,
  Stack,
  Alert,
  Button,
  Group
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useParams, useNavigate } from 'react-router-dom';

import ErrorState from '../components/state/ErrorState';
import LoadingState from '../components/state/LoadingState';
import SurveyForm from '../components/survey/SurveyForm';
import { type Survey } from '../components/survey/types';
import { usePostJSON } from '../utils/request';

interface SurveyResponse {
  success: boolean;
  survey: Survey;
}

const EditSurvey: React.FC = () => {
  const { id: _id } = useParams<{ id: string }>();
  const id = _id ? parseInt(_id, 10) : undefined;
  const navigate = useNavigate();

  // Fetch survey data using the /data_one endpoint
  const { data: surveyResponse, error, loading } = usePostJSON<SurveyResponse>(
    '/api/survey/data_one',
    { id },
    [id]
  );

  const survey = surveyResponse?.survey || null;

  // Handle missing ID
  if (!id) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Survey</Title>
        <Paper withBorder p="md">
          <ErrorState
            message="Survey ID is required"
            style={{ minHeight: '200px' }}
            title="Invalid Survey ID"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/surveys')}>
              Back to Surveys
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  const handleSuccess = () => {
    notifications.show({
      title: 'Success',
      message: 'Survey updated successfully',
      color: 'green'
    });
    void navigate('/surveys');
  };

  if (loading) {
    return (
      <LoadingState 
        message="Loading survey data..." 
        style={{ height: '50vh' }} 
      />
    );
  }

  if (error) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Survey</Title>
        <Paper withBorder p="md">
          <ErrorState
            message={error.message}
            style={{ minHeight: '200px' }}
            title="Error Loading Survey"
          />
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/surveys')}>
              Back to Surveys
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  if (!survey) {
    return (
      <Stack gap="md">
        <Title order={1}>Edit Survey</Title>
        <Paper withBorder p="md">
          <Alert color="red" title="Survey Not Found">
            The requested survey could not be found.
          </Alert>
          <Group justify="center" mt="md">
            <Button variant="outline" onClick={() => void navigate('/surveys')}>
              Back to Surveys
            </Button>
          </Group>
        </Paper>
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Group align="center" justify="space-between">
        <Title order={1}>Edit Survey</Title>
        <Button variant="outline" onClick={() => void navigate('/surveys')}>
          Back to All Surveys
        </Button>
      </Group>

      <Paper withBorder p="xl">
        <Stack gap="lg">
          <SurveyForm
            initialData={survey}
            mode="edit"
            onCancel={() => void navigate('/surveys')}
            onSuccess={handleSuccess}
          />
        </Stack>
      </Paper>
    </Stack>
  );
};

export default EditSurvey;

